import { CustomBodyCellProps } from '@app/types/main-table';
import { memo } from 'react';
import { getColumnOptions } from '@utils/tan-table.ts';
import { TableCell } from '@mui/material';
import { flexRender } from '@tanstack/react-table';

import styles from './index.module.scss';

function MainTableCellBasicComponent<TData extends Record<string, any>>({
  cell,
  isLeftScrollable,
  isRightScrollable,
}: CustomBodyCellProps<TData>) {
  if (!cell) return null;

  const { canResize, canPin, isPinned, isLeftPinned, isRightPinned } =
    getColumnOptions(cell.column);

  const tableCellClasses = [
    styles.tableCell,
    isPinned && canPin && styles.pinned,
    !isPinned && canResize && styles.canResize,
    isPinned && isLeftScrollable && styles.stickyLeft,
    isPinned && isRightScrollable && styles.stickyRight,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TableCell
      style={{
        left: isLeftPinned ? cell!.column.getStart('left') : undefined,
        right: isRightPinned ? cell.column.getAfter('right') : undefined,
        width: cell.column.getSize(),
      }}
      className={tableCellClasses}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
}

// Custom comparison function for MainTableCellBasic memo
const areEqual = <TData extends Record<string, any>>(
  prevProps: CustomBodyCellProps<TData>,
  nextProps: CustomBodyCellProps<TData>
) => {
  // Compare cell ID (unique identifier)
  if (prevProps.cell?.id !== nextProps.cell?.id) {
    return false;
  }

  // If either cell is null/undefined, they must both be
  if (!prevProps.cell || !nextProps.cell) {
    return prevProps.cell === nextProps.cell;
  }

  // Compare scroll states
  if (
    prevProps.isLeftScrollable !== nextProps.isLeftScrollable ||
    prevProps.isRightScrollable !== nextProps.isRightScrollable
  ) {
    return false;
  }

  // Compare column size (if column was resized)
  if (prevProps.cell.column.getSize() !== nextProps.cell.column.getSize()) {
    return false;
  }

  // Compare column pin state
  const prevPinned = prevProps.cell.column.getIsPinned();
  const nextPinned = nextProps.cell.column.getIsPinned();
  if (prevPinned !== nextPinned) {
    return false;
  }

  // Compare columnOrder array (if provided)
  if (prevProps.columnOrder !== nextProps.columnOrder) {
    if (!prevProps.columnOrder && !nextProps.columnOrder) {
      // Both undefined/null - equal
    } else if (
      !prevProps.columnOrder ||
      !nextProps.columnOrder ||
      prevProps.columnOrder.length !== nextProps.columnOrder.length
    ) {
      return false;
    } else {
      // Shallow array comparison
      for (let i = 0; i < prevProps.columnOrder.length; i++) {
        if (prevProps.columnOrder[i] !== nextProps.columnOrder[i]) {
          return false;
        }
      }
    }
  }

  // All checks passed - props are equal, skip re-render
  return true;
};

const MainTableCellBasic = memo(
  MainTableCellBasicComponent,
  areEqual
) as typeof MainTableCellBasicComponent;

export default MainTableCellBasic;
